
import {
    ICloseEditorsFilter, IEditorInput, IEditorGroup, IAddGroupOptions, ICloseEditorOptions,
    CloseDirection, IEditorOpenOptions_Input as IEditorOpenOptions, IMoveEditorOptions, GroupsOrder,
    EditorInput, IEditorGroupView, ICloseOptions, IEditorOptions,
} from '@workbench-stack/core';
import * as group from './editor-group-helper';
import * as parts from './editor-groups-helper';


export function openEditor(editor: IEditorInput, { editors = [], groups = [] }: IEditorGroupView, options?: IEditorOptions) {
    let editorId = editor.editorId || (parts.getActiveGroup(groups) || { id: parts.DEFAULT_GROUP_ID }).id;

    // Determine options
    const openEditorOptions: IEditorOpenOptions = {
        index: options ? options.index : undefined,
        pinned: editor.isPinned || (options && options.pinned) || (options && typeof options.index === 'number'),
        active: editors.length === 0 || !options || !options.inactive,
    };
    const activeEditor = group.activeEditor(editors);
    if (!openEditorOptions.active && !openEditorOptions.pinned && activeEditor && !activeEditor.isPinned) {
        // Special case: we are to open an editor inactive and not pinned, but the current active
        // editor is also not pinned, which means it will be replaced with this one. As such,
        // the editor can only be active.
        openEditorOptions.active = true;
    }

    let editorGroupOptions: IAddGroupOptions = {
        activate: false,
    };
    // Set group active unless we open inactive or preserve focus
    // Do this before we open the editor in the group to prevent a false
    // active editor change event before the editor is loaded
    // (see https://github.com/Microsoft/vscode/issues/51679)
    if (openEditorOptions.active && (!options || !options.preserveFocus)) {
        editorGroupOptions.activate = true;
    }
    const editorGroup = parts.getOrCreateGroup(editorId, groups, { activate: openEditorOptions.active });
    groups = parts.updateGroup(editorGroup, groups); // need to update the editorWrappers after adding new editor group

    // Actually move the editor if a speicifc index is provided and we figure
    // out that the editor is always opened at a different index. This
    // ensures the right set of events are fired to the outside.
    if (typeof openEditorOptions.index === 'number') {
        const indexOfEditor = group.indexOf(editor, editors);
        if (indexOfEditor !== -1 && indexOfEditor !== openEditorOptions.index) {
            if (indexOfEditor !== -1 && indexOfEditor !== openEditorOptions.index) {
                editors = moveEditorInsideGroup(editor, editors, openEditorOptions);
            }
        }
    }

    // Update model
    editors = group.openEditor(editor, editors, openEditorOptions);

    return { editors, groups };
}

export function moveEditorInsideGroup(editor: IEditorInput, editors: IEditorInput[], moveOptions?: IMoveEditorOptions) {

    const moveToIndex = moveOptions ? moveOptions.index : undefined;
    if (typeof moveToIndex !== 'number') {
        return editors; // do nothing if we move into same group without index
    }

    const currentIndex = group.indexOf(editor, editors);
    if (currentIndex === moveToIndex) {
        return editors; // do nothing if editor is already at the given index
    }
    // Update model
    editors = group.moveEditor(editor, editors, moveToIndex);
    editors = group.pin(editor, editors);

    // Forward to title area
    // TODO not sure we need anything here

    return editors;
}

export function openEditors(
    editors: { editor: IEditorInput, options?: IEditorOptions }[],
    { editors: stateEditors, groups }: IEditorGroupView,
) {
    if (!editors.length) {
        return editors;
    }

    // Do not modify original array
    editors = editors.slice(0);

    // Use the first editor as active editor
    const { editor, options } = editors.shift()!;
    let { editors: newEditors, groups: newGroups } = openEditor(editor, { editors: stateEditors, groups }, options);

    // Open the other ones inactive
    const startingIndex = group.indexOf(editor, stateEditors) + 1;

    editors.map(({ editor: otherEditor, options: otherOptions }, index) => {
        const adjustEditorOptions = {
            ...otherOptions,
            inactive: true,
            pinned: true,
            index: startingIndex + index,
        };

        const newData = openEditor(otherEditor, { editors: newEditors, groups: newGroups }, adjustEditorOptions);
        newEditors = newData.editors;
        newGroups = newData.groups;
    });

    return { editors: newEditors, groups: newGroups };
}

export function getEditorsToClose(editors: EditorInput[] | ICloseEditorsFilter, openedEditors: IEditorInput[]): IEditorInput[] {
    if (Array.isArray(editors)) {
        return openedEditors;
    }

    const filter = editors;
    const hasDirection = typeof filter.direction === 'number';

    let editorsToClose = openedEditors;

    // Filter: saved only
    if (filter.savedOnly) {
        editorsToClose = editorsToClose.filter(e => !e.fileReference.dirty);
    }

    // Filter: direction (left / right)
    // tslint:disable-next-line:one-line
    else if (hasDirection && filter.except) {
        editorsToClose = (filter.direction === CloseDirection.LEFT) ?
            editorsToClose.slice(0, openedEditors.indexOf(filter.except)) :
            editorsToClose.slice(openedEditors.indexOf(filter.except) + 1);
    }

    // Filter: except
    // tslint:disable-next-line:one-line
    else if (filter.except) {
        editorsToClose = editorsToClose.filter(e => e.resource !== filter.except.resource);
    }
    return editorsToClose;
}

// export function closeEditors(args: EditorInput[] | ICloseEditorsFilter,
//     { editors: stateEditors, groups }: IEditorGroupView,
//     options?: ICloseEditorOptions) {
//     const editors = getEditorsToClose(args, stateEditors );

//     // Check for dirty and veto
//     // TODO
// }

export function closeEditors(editors: EditorInput[], { editors: editorsState, groups }: IEditorGroupView, options?: ICloseOptions) {

    // Close all inactive editors first
    let shouldCloseActiveEditor = false;
    editors.forEach(editor => {
        if (!group.activateEditor) {
            editorsState = closeInActiveEditor(editor, editorsState);
        } else {
            shouldCloseActiveEditor = true;
        }
    });

    // Close active editor last if contained in editors list to close
    if (shouldCloseActiveEditor) {
        const { editors: newEditors, groups: newGroups } =
            closeActiveEditor({ editors: editorsState, groups }, options ? options.closeEmptyGroups : true,
                options && options.preserveFocus ? false : undefined,
            );
        editorsState = newEditors;
        groups = newGroups;
    }
    return { editors: editorsState, groups };
}

export function closeInActiveEditor(editor: EditorInput, editors: IEditorInput[]) {
    return group.closeEditor(editor, editors);
}

function closeActiveEditor({ editors, groups }: IEditorGroupView, focusNext, closeEmptyGroups?: boolean, fromError?: boolean) {
    const editorToClose = group.activeEditor(editors);


    // Optimization: if we are about to close the last editor in this group and settings
    // are configured to close the group since it will be empty, we first set the last
    // active group as empty before closing the editor. This reduces the amount of editor
    // change events that this operation emits and will reduce flicker. Without this
    // optimization, this group (if active) would first trigger a active editor change
    // event because it became empty, only to then trigger another one when the next
    // group gets active.
    if (closeEmptyGroups && editorToClose.isActive && editors.length === 1) {
        const mostRecentlyActiveGroups = parts.getGroups(GroupsOrder.MOST_RECENTLY_ACTIVE, groups);
        const nextActiveGroup = mostRecentlyActiveGroups[1]; // [0] will be the current one, so take [1]
        if (nextActiveGroup) {
            // if (restoreFocus) {
            //     nextActiveGroup.focus();
            // } else {
            groups = parts.activateGroup(nextActiveGroup, groups);
            // }
        }
    }

    // Update model
    if (editorToClose) {
        editors = group.closeEditor(editorToClose, editors);
    }

    // Open next active if there are more to show
    const nextActiveEditor = group.activeEditor(editors);
    if (nextActiveEditor) {
        const options = { preserveFocus: !focusNext, ignoreError: false };


        // When closing an editor due to an error we can end up in a loop where we continue closing
        // editors that fail to open (e.g. when the file no longer exists). We do not want to show
        // repeated errors in this case to the user. As such, if we open the next editor and we are
        // in a scope of a previous editor failing, we silence the input errors until the editor is
        // opened by setting ignoreError: true
        if (fromError) {
            options.ignoreError = true;
        }
        const { editors: newEditors, groups: newGroups } = openEditor(nextActiveEditor, { editors, groups }, options);
        editors = newEditors;
        groups = newGroups;
    }

    // Otherwise we are empty, so clear from editor control and send event
    // tslint:disable-next-line:one-line
    else {
        // Forward to editor control
        if (editorToClose) {
            // close editor controls or antying if needed
        }
        // Restore focus to group container as needed unless group gets closed
        // if (restoreFocus && !closeEmptyGroups) {
        //     // focus the group
        //     parts.focus();
        // }

        // Remvoe empty group if we should
        if (closeEmptyGroups) {
            groups = parts.removeGroup(editorToClose.editorId, groups);
            delete groups[editorToClose.editorId];
        }

    }
    return { editors, groups };
}


// function handleDirty(editor: IEditorInput) {
//     if (!editor.dirty || // editor must be dirty
//         )
// }
